/**
 * GovernanceRuntimeMap
 *
 * Signature hero visual for LeftShield — a horizontal pipeline showing live
 * governance flow from data collection through framework compliance.
 *
 * Five pipeline stages:
 *   Collectors -> Enforcement -> Controls -> Evidence -> Frameworks
 *
 * Supports two variants:
 *   - "compact"  — single row, minimal metrics
 *   - "expanded" — full detail with secondary metrics
 *
 * Pure CSS animations with prefers-reduced-motion support.
 * No external charting libraries.
 */
import React, { useMemo } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Card,
  Body2,
  Caption1,
  Subtitle2,
  shorthands,
} from '@fluentui/react-components';
import {
  PlugConnected24Regular,
  ShieldCheckmark24Regular,
  Grid24Regular,
  Certificate24Regular,
  DocumentBulletList24Regular,
} from '@fluentui/react-icons';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type NodeStatus = 'healthy' | 'degraded' | 'inactive';

interface CollectorData {
  count: number;
  status: NodeStatus;
  delayed?: number;
}

interface EnforcementData {
  policiesActive: number;
  evaluationsToday: number;
  violations: number;
  status: NodeStatus;
}

interface ControlsData {
  mapped: number;
  coverage: number;
  status: NodeStatus;
}

interface EvidenceData {
  records: number;
  integrityScore: number;
  status: NodeStatus;
}

interface FrameworksData {
  names: string[];
  avgCoverage: number;
  status: NodeStatus;
}

export interface GovernanceRuntimeMapProps {
  variant?: 'compact' | 'expanded';
  data?: {
    collectors: CollectorData;
    enforcement: EnforcementData;
    controls: ControlsData;
    evidence: EvidenceData;
    frameworks: FrameworksData;
  };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<NodeStatus, string> = {
  healthy: '#22C55E',
  degraded: '#F59E0B',
  inactive: '#EF4444',
};

const SYSTEM_COLOR = '#0078D4';

const DEFAULT_DATA: NonNullable<GovernanceRuntimeMapProps['data']> = {
  collectors: { count: 7, status: 'healthy', delayed: 1 },
  enforcement: { policiesActive: 24, evaluationsToday: 12903, violations: 17, status: 'healthy' },
  controls: { mapped: 82, coverage: 79, status: 'healthy' },
  evidence: { records: 13294, integrityScore: 97, status: 'healthy' },
  frameworks: { names: ['SOC2', 'ISO27001'], avgCoverage: 78, status: 'healthy' },
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

/**
 * CSS keyframes are injected via a <style> tag in the component because
 * Griffel (Fluent UI v9 makeStyles) does not natively support @keyframes
 * declarations. The animations are scoped with unique names to avoid
 * collisions.
 */
const KEYFRAMES_ID = 'governance-runtime-map-keyframes';

const keyframesCSS = `
@keyframes grm-pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.3); }
}

@keyframes grm-flow-arrow {
  0% { opacity: 0.3; transform: translateX(-4px); }
  50% { opacity: 1; transform: translateX(4px); }
  100% { opacity: 0.3; transform: translateX(-4px); }
}

@keyframes grm-arrow-dash {
  0% { stroke-dashoffset: 16; }
  100% { stroke-dashoffset: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .grm-pulse-dot,
  .grm-flow-arrow,
  .grm-arrow-dash {
    animation: none !important;
  }
}
`;

const useStyles = makeStyles({
  // Root container
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },

  // Pipeline row — horizontal layout with nodes + arrows
  pipeline: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0px', // arrows sit in between nodes
    overflowX: 'auto',
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
  },

  // Individual node card
  node: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    minWidth: '140px',
    maxWidth: '180px',
    flexShrink: 0,
    position: 'relative' as const,
    transitionProperty: 'box-shadow, transform',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      boxShadow: tokens.shadow8,
      transform: 'translateY(-2px)',
    },
  },

  nodeCompact: {
    minWidth: '110px',
    maxWidth: '140px',
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    gap: tokens.spacingVerticalXS,
  },

  // Icon wrapper
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground2,
  },

  iconWrapperCompact: {
    width: '32px',
    height: '32px',
  },

  // Node label
  nodeLabel: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    textAlign: 'center' as const,
  },

  // Primary metric
  metricValue: {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    fontVariantNumeric: 'tabular-nums',
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightBold,
    lineHeight: tokens.lineHeightBase500,
    color: tokens.colorNeutralForeground1,
  },

  metricValueCompact: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
  },

  // Status dot
  statusDot: {
    width: '8px',
    height: '8px',
    ...shorthands.borderRadius('50%'),
    flexShrink: 0,
  },

  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },

  // Secondary metric
  secondaryMetric: {
    color: tokens.colorNeutralForeground3,
    textAlign: 'center' as const,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },

  // Arrow connector
  arrowConnector: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    flexShrink: 0,
  },

  arrowConnectorCompact: {
    width: '32px',
  },

  // Section header (for expanded mode)
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface PipelineNodeProps {
  icon: React.ReactElement;
  label: string;
  primaryMetric: string;
  status: NodeStatus;
  secondaryMetric?: string;
  compact?: boolean;
  accentColor?: string;
}

const PipelineNode: React.FC<PipelineNodeProps> = ({
  icon,
  label,
  primaryMetric,
  status,
  secondaryMetric,
  compact = false,
  accentColor,
}) => {
  const styles = useStyles();
  const statusColor = STATUS_COLORS[status];

  return (
    <div
      className={mergeClasses(styles.node, compact && styles.nodeCompact)}
      role="group"
      aria-label={`${label}: ${primaryMetric}, status ${status}`}
    >
      <div
        className={mergeClasses(styles.iconWrapper, compact && styles.iconWrapperCompact)}
        style={accentColor ? { backgroundColor: `${accentColor}18` } : undefined}
      >
        <span style={{ color: accentColor || SYSTEM_COLOR, fontSize: compact ? '18px' : '22px', display: 'flex' }}>
          {icon}
        </span>
      </div>

      {!compact && (
        <Body2 className={styles.nodeLabel}>{label}</Body2>
      )}
      {compact && (
        <Caption1 className={styles.nodeLabel}>{label}</Caption1>
      )}

      <span
        className={mergeClasses(
          styles.metricValue,
          compact && styles.metricValueCompact,
          'metric-value',
        )}
      >
        {primaryMetric}
      </span>

      <div className={styles.statusRow}>
        <span
          className={mergeClasses(styles.statusDot, 'grm-pulse-dot')}
          style={{
            backgroundColor: statusColor,
            animation: status === 'healthy'
              ? 'grm-pulse-dot 2s ease-in-out infinite'
              : status === 'degraded'
                ? 'grm-pulse-dot 1s ease-in-out infinite'
                : 'none',
          }}
          aria-hidden="true"
        />
        <Caption1 style={{ color: statusColor, fontWeight: 600 }}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Caption1>
      </div>

      {secondaryMetric && !compact && (
        <Caption1 className={styles.secondaryMetric}>{secondaryMetric}</Caption1>
      )}
    </div>
  );
};

/**
 * Animated arrow connector between pipeline nodes.
 * Uses an inline SVG with a dashed line + chevron.
 */
const ArrowConnector: React.FC<{ compact?: boolean; healthy?: boolean }> = ({
  compact = false,
  healthy = true,
}) => {
  const styles = useStyles();
  const width = compact ? 32 : 48;
  const strokeColor = healthy ? STATUS_COLORS.healthy : STATUS_COLORS.degraded;

  return (
    <div
      className={mergeClasses(
        styles.arrowConnector,
        compact && styles.arrowConnectorCompact,
      )}
      aria-hidden="true"
    >
      <svg
        width={width}
        height="24"
        viewBox={`0 0 ${width} 24`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Dashed flow line */}
        <line
          x1="0"
          y1="12"
          x2={width - 10}
          y2="12"
          stroke={strokeColor}
          strokeWidth="2"
          strokeDasharray="6 4"
          className="grm-arrow-dash"
          style={{
            animation: 'grm-arrow-dash 0.8s linear infinite',
          }}
        />
        {/* Chevron head */}
        <polyline
          points={`${width - 12},7 ${width - 4},12 ${width - 12},17`}
          stroke={strokeColor}
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="grm-flow-arrow"
          style={{
            animation: 'grm-flow-arrow 1.5s ease-in-out infinite',
          }}
        />
      </svg>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function formatNumber(n: number): string {
  if (n >= 10000) {
    return `${(n / 1000).toFixed(1)}k`;
  }
  return n.toLocaleString();
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const GovernanceRuntimeMap: React.FC<GovernanceRuntimeMapProps> = ({
  variant = 'expanded',
  data,
}) => {
  const styles = useStyles();
  const compact = variant === 'compact';
  const d = useMemo(() => data ?? DEFAULT_DATA, [data]);

  // Inject keyframes once into the document head
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById(KEYFRAMES_ID)) return;
    const style = document.createElement('style');
    style.id = KEYFRAMES_ID;
    style.textContent = keyframesCSS;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(KEYFRAMES_ID);
      if (el) el.remove();
    };
  }, []);

  // Build node descriptors
  const nodes = useMemo(() => {
    const delayedSuffix = d.collectors.delayed
      ? ` (${d.collectors.delayed} delayed)`
      : '';

    return [
      {
        key: 'collectors',
        icon: <PlugConnected24Regular />,
        label: 'Collectors',
        primaryMetric: String(d.collectors.count),
        status: d.collectors.status,
        secondaryMetric: `${d.collectors.count} connected${delayedSuffix}`,
        accentColor: '#E89652', // Environment orange
      },
      {
        key: 'enforcement',
        icon: <ShieldCheckmark24Regular />,
        label: 'Enforcement',
        primaryMetric: String(d.enforcement.policiesActive),
        status: d.enforcement.status,
        secondaryMetric: `${formatNumber(d.enforcement.evaluationsToday)} evals | ${d.enforcement.violations} violations`,
        accentColor: '#E85252', // Risk red
      },
      {
        key: 'controls',
        icon: <Grid24Regular />,
        label: 'Controls',
        primaryMetric: `${d.controls.coverage}%`,
        status: d.controls.status,
        secondaryMetric: `${d.controls.mapped} mapped controls`,
        accentColor: '#13B5B1', // Compliance cyan
      },
      {
        key: 'evidence',
        icon: <Certificate24Regular />,
        label: 'Evidence',
        primaryMetric: formatNumber(d.evidence.records),
        status: d.evidence.status,
        secondaryMetric: `${d.evidence.integrityScore}% integrity`,
        accentColor: '#8852E0', // AI purple
      },
      {
        key: 'frameworks',
        icon: <DocumentBulletList24Regular />,
        label: 'Frameworks',
        primaryMetric: `${d.frameworks.avgCoverage}%`,
        status: d.frameworks.status,
        secondaryMetric: d.frameworks.names.join(', '),
        accentColor: SYSTEM_COLOR,
      },
    ] as const;
  }, [d]);

  // Determine whether the arrow between two adjacent nodes is "healthy"
  // (both sides healthy) or degraded.
  const isArrowHealthy = (leftIdx: number): boolean => {
    const leftStatus = nodes[leftIdx].status;
    const rightStatus = nodes[leftIdx + 1].status;
    return leftStatus === 'healthy' && rightStatus === 'healthy';
  };

  return (
    <div className={styles.root} role="region" aria-label="Governance Runtime Pipeline">
      {!compact && (
        <div className={styles.sectionHeader}>
          <Subtitle2>Governance Pipeline</Subtitle2>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            Real-time data flow
          </Caption1>
        </div>
      )}

      <Card>
        <div className={styles.pipeline}>
          {nodes.map((node, idx) => (
            <React.Fragment key={node.key}>
              <PipelineNode
                icon={node.icon}
                label={node.label}
                primaryMetric={node.primaryMetric}
                status={node.status}
                secondaryMetric={node.secondaryMetric}
                compact={compact}
                accentColor={node.accentColor}
              />
              {idx < nodes.length - 1 && (
                <ArrowConnector compact={compact} healthy={isArrowHealthy(idx)} />
              )}
            </React.Fragment>
          ))}
        </div>
      </Card>
    </div>
  );
};
